import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import useScrollToTop from "../../../hooks/useScollToTop";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import Button from "../../../components/Button/Button";
import ButtonEdit from "../../../components/Button/ButtonEdit";
import ButtonDelete from "../../../components/Button/ButtonDelete";
import { ToastContainer, toast } from "react-toastify";
import {
  getHighLevelSessionsByAdherent,
  deleteHighLevelSession,
  toAbsoluteAdherentPhotoUrl,
} from "../../../services/highLevelSessionServices";
import { getAdherentsByUser } from "../../../services/adherentServices";
import {
  FaSearch,
  FaPlus,
  FaInfoCircle,
  FaUserCircle,
  FaFilter,
  FaRedo,
} from "react-icons/fa";

import AddHighLevelAdherentSessionModal from "./AddHighLevelAdherentSessionModal";
import EditHighLevelAdherentSessionModal from "./EditHighLevelAdherentSessionModal";

/* =========================
   Constantes & helpers
   ========================= */
const CATEGORIES = [
  "Entrainement Judo club",
  "Entrainement Structure",
  "Technique club",
  "Technique structure",
  "Musculation",
  "Course ",
  "Prépa associé ou dissocié",
  "Stage ou Training camp",
  "Competition",
];

const fmt = (d, f = "DD/MM/YY") => (moment(d).isValid() ? moment(d).format(f) : "—");

/**
 * Structure interne des filtres par adhérent :
 * {
 *   [adherentId]: { search, categorie_seance, is_done, date_from, date_to, loading }
 * }
 */
export default function HighLevelAdherentSessionsPage() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [loading, setLoading] = useState(true);
  const [adherents, setAdherents] = useState([]);
  const [sessionsByAdherent, setSessionsByAdherent] = useState({});
  const [filters, setFilters] = useState({});
  const [addingFor, setAddingFor] = useState(null); // {id, nom, prenom, ...}
  const [editing, setEditing] = useState(null); // session object

  /** Charge la liste des adhérents rattachés à l'utilisateur */
  const loadAdherents = useCallback(async () => {
    setLoading(true);
    try {
      const list = await getAdherentsByUser();
      const safe = Array.isArray(list) ? list : [];
      safe.sort((a, b) =>
        `${a?.nom || ""} ${a?.prenom || ""}`.localeCompare(
          `${b?.nom || ""} ${b?.prenom || ""}`,
          "fr",
          { sensitivity: "base" }
        )
      );
      setAdherents(safe);

      // Initialise des filtres par défaut pour chacun
      const base = {};
      safe.forEach((ad) => {
        base[ad.id] = {
          search: "",
          categorie_seance: "",
          is_done: "",
          date_from: "",
          date_to: "",
          loading: true,
        };
      });
      setFilters(base);

      // Charge les séances de chaque adhérent (avec filtres initiaux vides)
      const entries = await Promise.all(
        safe.map(async (ad) => {
          try {
            const data = await getHighLevelSessionsByAdherent(ad.id, { limit: 500 });
            return [ad.id, Array.isArray(data) ? data : []];
          } catch (e) {
            console.error("Erreur sessions:", e);
            return [ad.id, []];
          }
        })
      );
      setSessionsByAdherent(Object.fromEntries(entries));
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors du chargement des adhérents.");
    } finally {
      // enlève loading pour tous
      setFilters((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((k) => (next[k] = { ...next[k], loading: false }));
        return next;
      });
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAdherents();
  }, [loadAdherents]);

  /** Applique les filtres côté API pour un adhérent donné */
  const applyFilters = async (adherentId) => {
    setFilters((p) => ({ ...p, [adherentId]: { ...p[adherentId], loading: true } }));
    try {
      const f = filters[adherentId] || {};
      const payload = {
        categorie_seance: f.categorie_seance || undefined,
        is_done: f.is_done !== "" ? f.is_done : undefined,
        date_from: f.date_from || undefined,
        date_to: f.date_to || undefined,
        limit: 500,
      };
      const data = await getHighLevelSessionsByAdherent(adherentId, payload);
      setSessionsByAdherent((prev) => ({ ...prev, [adherentId]: Array.isArray(data) ? data : [] }));
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l'application des filtres.");
    } finally {
      setFilters((p) => ({ ...p, [adherentId]: { ...p[adherentId], loading: false } }));
    }
  };

  /** Réinitialise les filtres d'un adhérent + recharge */
  const resetFilters = async (adherentId) => {
    setFilters((p) => ({
      ...p,
      [adherentId]: { search: "", categorie_seance: "", is_done: "", date_from: "", date_to: "", loading: true },
    }));
    try {
      const data = await getHighLevelSessionsByAdherent(adherentId, { limit: 500 });
      setSessionsByAdherent((prev) => ({ ...prev, [adherentId]: Array.isArray(data) ? data : [] }));
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors du rechargement.");
    } finally {
      setFilters((p) => ({ ...p, [adherentId]: { ...p[adherentId], loading: false, search: "" } }));
    }
  };

  /** Change handler générique pour filtres */
  const onFilterChange = (adherentId, name, value) => {
    setFilters((p) => ({ ...p, [adherentId]: { ...p[adherentId], [name]: value } }));
  };

  /** Filtrage local (recherche plein texte) */
  const makeFilteredList = (adherentId) => {
    const list = sessionsByAdherent[adherentId] || [];
    const f = filters[adherentId] || {};
    const q = (f.search || "").trim().toLowerCase();
    if (!q) return list;
    return list.filter((it) => {
      const ns = `${it?.nom_seance || ""}`.toLowerCase();
      const cat = `${it?.categorie_seance || ""}`.toLowerCase();
      const obs = `${it?.observations_seance || ""}`.toLowerCase();
      return ns.includes(q) || cat.includes(q) || obs.includes(q);
    });
  };

  const onDeleted = async (sessionId, adherentId) => {
    if (!window.confirm("Supprimer cette séance ?")) return;
    try {
      await deleteHighLevelSession(sessionId);
      await applyFilters(adherentId);
      toast.success("Séance supprimée");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <section className="w-full">
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Titre page */}
        <HeaderTitle role="Utilisateur" title="Suivi Haut Niveau – Mes adhérents" align="center" className="mb-1" />

        {/* Navigation secondaire */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale (intro) */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-6">
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 text-white">
              <h2 className="text-lg sm:text-xl font-semibold text-center">Suivez et enregistrez vos séances</h2>
              <p className="text-sm/6 opacity-95 max-w-3xl mx-auto text-center mt-1">
                Si votre compte gère plusieurs adhérents, chaque bloc ci-dessous correspond à l’un d’eux.
              </p>
              <div className="px-5 py-3 sm:px-8">
                <ul className="list-disc pl-5 text-sm">
                  <li>Filtrez par catégorie / statut / période. Cherchez par nom de séance.</li>
                  <li>Ajoutez vos séances, modifiez ou supprimez-les facilement.</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Contenu adherents */}
          <div className="px-5 pb-6 sm:px-8">
            {loading ? (
              <div className="space-y-4">
                {[...Array(2)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse h-36 rounded-2xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white/70 dark:bg-slate-900/50"
                  />
                ))}
              </div>
            ) : adherents.length === 0 ? (
              <div className="text-center py-12 text-slate-600 dark:text-slate-400">
                Aucun adhérent rattaché à votre compte.
              </div>
            ) : (
              <div className="space-y-6">
                {adherents.map((ad) => {
                  const avatar =
                    toAbsoluteAdherentPhotoUrl(ad?.photo_url) || null;
                  const f = filters[ad.id] || {};
                  const list = makeFilteredList(ad.id);
                  return (
                    <div
                      key={ad.id}
                      className="rounded-2xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-800 bg-white/90 dark:bg-slate-900/60"
                    >
                      {/* Header carte adhérent */}
                      <div className="px-5 py-3 bg-slate-50/70 dark:bg-slate-900/70 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                          {avatar ? (
                            <img
                              src={avatar}
                              alt={`${ad.prenom} ${ad.nom}`}
                              className="h-10 w-10 rounded-full object-cover ring-2 ring-slate-200 dark:ring-slate-700"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-slate-500">
                              <FaUserCircle />
                            </div>
                          )}
                          <div className="truncate">
                            <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100 truncate">
                              {ad.prenom} {ad.nom}
                            </h3>
                            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                              {ad.email || ""}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Button
                            text="Ajouter une séance"
                            icon={FaPlus}
                            onClick={() => setAddingFor(ad)}
                            className="!rounded-xl"
                          />
                        </div>
                      </div>

                      {/* Zone filtres par adhérent */}
                      <div className="px-5 pt-4">
                        <div className="grid grid-cols-1 lg:grid-cols-6 gap-3">
                          {/* Recherche locale */}
                          <div className="lg:col-span-2">
                            <div className="relative">
                              <input
                                type="text"
                                placeholder="Rechercher (nom, catégorie, note)…"
                                value={f.search || ""}
                                onChange={(e) => onFilterChange(ad.id, "search", e.target.value)}
                                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                              />
                              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            </div>
                          </div>

                          {/* Catégorie */}
                          <div>
                            <select
                              value={f.categorie_seance || ""}
                              onChange={(e) => onFilterChange(ad.id, "categorie_seance", e.target.value)}
                              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
                            >
                              <option value="">Toutes catégories</option>
                              {CATEGORIES.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Statut */}
                          <div>
                            <select
                              value={f.is_done || ""}
                              onChange={(e) => onFilterChange(ad.id, "is_done", e.target.value)}
                              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
                            >
                              <option value="">Tous statuts</option>
                              <option value="true">Terminé</option>
                              <option value="false">Planifié</option>
                            </select>
                          </div>

                          {/* Dates */}
                          <div>
                            <input
                              type="date"
                              value={f.date_from || ""}
                              onChange={(e) => onFilterChange(ad.id, "date_from", e.target.value)}
                              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
                            />
                          </div>
                          <div>
                            <input
                              type="date"
                              value={f.date_to || ""}
                              onChange={(e) => onFilterChange(ad.id, "date_to", e.target.value)}
                              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
                            />
                          </div>
                        </div>

                        {/* Actions filtres */}
                        <div className="mt-3 flex items-center gap-2">
                          <Button
                            text="Appliquer filtres"
                            icon={FaFilter}
                            onClick={() => applyFilters(ad.id)}
                            className="!rounded-xl"
                          />
                          <button
                            type="button"
                            onClick={() => resetFilters(ad.id)}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-700 text-sm"
                          >
                            <FaRedo /> Réinitialiser
                          </button>
                        </div>
                      </div>

                      {/* Liste des séances */}
                      <div className="p-4 sm:p-6">
                        {f.loading ? (
                          <div className="space-y-3">
                            {[...Array(3)].map((_, i) => (
                              <div
                                key={i}
                                className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-20"
                              />
                            ))}
                          </div>
                        ) : list.length === 0 ? (
                          <div className="text-center py-10 text-slate-600 dark:text-slate-400">
                            Aucune séance pour cet adhérent.
                          </div>
                        ) : (
                          <>
                            {/* Table Desktop */}
                            <div className="hidden md:block">
                              <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
                                <div className="overflow-x-auto">
                                  <table className="min-w-full">
                                    <thead>
                                      <tr className="bg-slate-50/80 dark:bg-slate-900/80 uppercase tracking-wide text-xs">
                                        {[
                                          "Catégorie",
                                          "Séance",
                                          "Lieu",
                                          "Date",
                                          "Durée",
                                          "Qualité",
                                          "Statut",
                                          "Actions",
                                        ].map((h) => (
                                          <th
                                            key={h}
                                            className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white"
                                          >
                                            {h}
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                                      {list.map((it) => (
                                        <tr
                                          key={it.id}
                                          className="group transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                                        >
                                          <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                                            {it?.categorie_seance || "—"}
                                          </td>
                                          <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                                            {it?.nom_seance || "—"}
                                          </td>
                                          <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                                            {it?.lieu_seance || "—"}
                                          </td>
                                          <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                                            {fmt(it?.seance_date)}
                                          </td>
                                          <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                                            {it?.duree_minutes ?? 0} min
                                          </td>
                                          <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                                            {it?.qualite_note ?? "—"}
                                          </td>
                                          <td className="px-4 py-3">
                                            <span
                                              className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold ${
                                                it?.is_done
                                                  ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
                                                  : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                                              }`}
                                            >
                                              {it?.is_done ? "Terminé" : "Planifié"}
                                            </span>
                                          </td>
                                          <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                              <ButtonEdit onClick={() => setEditing({ ...it, _adherent: ad })} />
                                              <ButtonDelete onClick={() => onDeleted(it.id, ad.id)} />
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>

                            {/* Cartes Mobile */}
                            <div className="md:hidden space-y-3">
                              {list.map((it) => (
                                <div
                                  key={`card-${it.id}`}
                                  className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-4 shadow-sm transition hover:shadow-md"
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div>
                                      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                        {it?.categorie_seance || "—"}
                                      </div>
                                      <div className="text-xs text-slate-500 dark:text-slate-400">
                                        {fmt(it?.seance_date)}
                                      </div>
                                    </div>
                                    <span
                                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                        it?.is_done
                                          ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
                                          : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                                      }`}
                                    >
                                      {it?.is_done ? "Terminé" : "Planifié"}
                                    </span>
                                  </div>

                                  <div className="mt-2 text-sm text-slate-700 dark:text-slate-300">
                                    <div>
                                      <span className="font-medium">Séance :</span> {it?.nom_seance || "—"}
                                    </div>
                                    <div>
                                      <span className="font-medium">Lieu :</span> {it?.lieu_seance || "—"}
                                    </div>
                                    <div>
                                      <span className="font-medium">Durée :</span>{" "}
                                      {it?.duree_minutes ?? 0} min
                                    </div>
                                    <div>
                                      <span className="font-medium">Qualité :</span>{" "}
                                      {it?.qualite_note ?? "—"}
                                    </div>
                                    {!!it?.observations_seance && (
                                      <div className="mt-1">
                                        <span className="font-medium">Obs. :</span>{" "}
                                        {it.observations_seance}
                                      </div>
                                    )}
                                  </div>

                                  <div className="mt-3 flex items-center gap-2">
                                    <ButtonEdit onClick={() => setEditing({ ...it, _adherent: ad })} />
                                    <ButtonDelete onClick={() => onDeleted(it.id, ad.id)} />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modales globales */}
      {addingFor && (
        <AddHighLevelAdherentSessionModal
          isOpen={!!addingFor}
          onClose={() => setAddingFor(null)}
          adherent={addingFor}
          categories={CATEGORIES}
          afterSave={async () => {
            if (addingFor?.id) await applyFilters(addingFor.id);
            setAddingFor(null);
          }}
        />
      )}

      {editing && (
        <EditHighLevelAdherentSessionModal
          isOpen={!!editing}
          onClose={() => setEditing(null)}
          session={editing}
          adherent={editing?._adherent}
          categories={CATEGORIES}
          afterSave={async () => {
            if (editing?._adherent?.id) await applyFilters(editing._adherent.id);
            setEditing(null);
          }}
        />
      )}

      <ToastContainer />
    </section>
  );
}
